import {IS_SERVER} from '@github-ui/ssr-utils'
import type {createLowlight} from 'lowlight'
import {useEffect, useState} from 'react'

export type LowlightInstance = ReturnType<typeof createLowlight>

let cachedLowlight: LowlightInstance | null = null
let lowlightPromise: Promise<LowlightInstance | null> | null = null

export function loadLowlight(): Promise<LowlightInstance | null> {
  if (IS_SERVER) return Promise.resolve(null)
  if (cachedLowlight !== null) return Promise.resolve(cachedLowlight)
  if (lowlightPromise !== null) return lowlightPromise
  lowlightPromise = (async () => {
    try {
      const mod = await import('lowlight')
      cachedLowlight = mod.createLowlight(mod.all)
      return cachedLowlight
    } catch {
      lowlightPromise = null
      return null
    }
  })()
  return lowlightPromise
}

export function useLowlight(enabled = true): LowlightInstance | null {
  const [instance, setInstance] = useState<LowlightInstance | null>(cachedLowlight)
  useEffect(() => {
    // Always attempt a load when enabled: loadLowlight() resolves immediately from
    // the module cache when it's already populated, so a consumer whose `enabled`
    // flips false->true after another consumer cached lowlight still syncs the
    // instance (instead of staying null and rendering plain text).
    if (!enabled) return
    let cancelled = false
    void (async () => {
      const l = await loadLowlight()
      if (l && !cancelled) setInstance(l)
    })()
    return () => {
      cancelled = true
    }
  }, [enabled])
  return enabled ? instance : null
}

// a curated common subset as using `all` causes frequent misdetection on short snippets.
export const HIGHLIGHT_SUBSET = [
  'bash',
  'c',
  'cpp',
  'csharp',
  'css',
  'dart',
  'diff',
  'dockerfile',
  'go',
  'haskell',
  'hcl',
  'html',
  'java',
  'javascript',
  'json',
  'jsx',
  'kotlin',
  'lua',
  'markdown',
  'perl',
  'php',
  'powershell',
  'python',
  'r',
  'ruby',
  'rust',
  'scala',
  'shell',
  'sql',
  'swift',
  'tsx',
  'typescript',
  'xml',
  'yaml',
]
